/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, debounce, Typography } from "@mui/material";
import { Edit, Trash } from "@jda/lui-common-icon-library-mui5";
import { LuiThreeDotMenu, LuiThreeDotMenuThumbnailType } from "@jda/lui-common-component-library-mui5";

import { useStyles } from "./styles";
import { MESSAGES, TITLES } from "./constants";
import Loader from "../../shared/loader/Loader";
import SearchBar from "../../shared/searchLists/SearchBar";
import ModuleDetails from "../moduleDetails/ModuleDetails";
import SearchList from "../../shared/searchLists/SearchList";
import { AlertDialog } from "../../shared/dialog/AlertDialog";
import AddModuleDialog from "../addModuleDialog/AddModuleDialog";
import { actions, resources } from "../checkPermission/constants";
import { getModules, removeModule } from "../../store/module/module";
import SearchListItem from "../../shared/searchLists/SearchListItem";
import EmptyComponent from "../../shared/emptyComponent/EmptyComponent";
import { selectLoaderComponent } from "../../store/loader/loaderSelector";
import { ThreeDotOption } from "../../shared/ThreeDotOption/ThreeDotOption";
import { selectAllAssignedPermissions } from "../../store/auth/authSelector";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";
import { setSelectedModuleAction } from "../../store/module/moduleActionCreator";
import { selectSelectedApplication } from "../../store/application/applicationSelector";
import { selectSelectedOrganization } from "../../store/organization/organizationSelector";
import { hideLoaderAction, showLoaderAction } from "../../store/loader/loaderActionCreator";
import { checkAuthzPermission, CheckAuthzPermissions } from "../checkPermission/CheckViewPermission";
import { selectAllModule, selectModulePagination, selectSelectedModule } from "../../store/module/moduleSelector";

const ModuleList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const moduleList = useSelector(selectAllModule);
  const selectedModule = useSelector(selectSelectedModule)
  const loaderComponent = useSelector(selectLoaderComponent);
  const selectedApplication = useSelector(selectSelectedApplication);
  const selectedOrganization = useSelector(selectSelectedOrganization);
  const selectedModulePagination = useSelector(selectModulePagination);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);

  const [action, setAction] = useState("");
  const [searchString, setSearchString] = useState("");

  const createApplicationPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.CREATE, assignedPermissions.resources[resources.APPLICATION]);

  const editModulePermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.EDIT, assignedPermissions.resources[resources.MODULE]);
  const deleteModulePermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.DELETE, assignedPermissions.resources[resources.MODULE]);

  useEffect(() => {
    setSearchString("")
    if (selectedApplication?.id) {
      getAllModuleList();
    }
  }, [selectedApplication?.id]);

  const getAllModuleList = async (pageNo?: number) => {
    if (selectedOrganization?.id && selectedApplication.id) {
      dispatch(showLoaderAction(TITLES.MODULES));
      const modulePayload = {
        organizationId: selectedOrganization?.id,
        applicationId: selectedApplication?.id,
        pageSize: 500,
        pageNo: pageNo || 1,
        searchString: '',
      }
      await getModules(dispatch, modulePayload);
      dispatch(hideLoaderAction(TITLES.MODULES));
    }
  };

  const onSearchTextBoxChange = (searchValue: string) => {
    setSearchString(searchValue);
    const modulePayload = {
      organizationId: selectedOrganization?.id,
      applicationId: selectedApplication?.id,
      pageSize: 500,
      pageNo: 1,
      searchString: searchValue,
    }
    if (searchValue.trim() !== "" && searchValue.length > 2) {
      getModules(dispatch, modulePayload);
    }
    dispatch(hideLoaderAction(TITLES.MODULES));
  };

  const handleClearSearch = () => {
    setSearchString("");
    const modulePayload = {
      organizationId: selectedOrganization?.id,
      applicationId: selectedApplication?.id,
      pageSize: 500,
      pageNo: 1,
      searchString: '',
    }
    getModules(dispatch, modulePayload);
  }

  const handleListItemClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, listItem: any) => {
    dispatch(setSelectedModuleAction(listItem));
  };

  const handleMenuClick = (actionName: string) => {
    setAction(actionName);
  };

  const handleCloseDialog = () => {
    setAction("");
  };

  useEffect(() => {
    return () => {
      debounceSearch.clear();
    };
  });

  const deleteModule = async () => {
    const payload = {
      id: selectedModule.id,
      organizationId: selectedOrganization.id,
      applicationId: selectedApplication.id
    }
    await removeModule(dispatch, payload);
    setAction("");
    dispatch(hideLoaderAction(MESSAGES.DELETE_MODULE_TITLE));
  };

  const items: LuiThreeDotMenuThumbnailType[] = [
    editModulePermission && {
      render: <ThreeDotOption icon={<Edit fontSize="small" />} text={TITLES.EDIT} />,
      onClick: () => handleMenuClick("Edit"),
      autoClose: true,
    },
    deleteModulePermission && {
      render: <ThreeDotOption icon={<Trash fontSize="small" />} text={TITLES.DELETE} />,
      onClick: () => handleMenuClick("Delete"),
      autoClose: true,
    },
  ];

  const debounceSearch = debounce(onSearchTextBoxChange, 500);
  const showLoader = false;

  return (
    <Box className={classes.moduleWrap}>
      <CheckAuthzPermissions action={actions.VIEW} resource={resources.MODULE}>
        <Box>
          <Box className={(moduleList && moduleList.length > 0) || (searchString.length) ? classes.root : classes.hide}>
            <Box className={classes.topLeftHeaderContainer}>
              <SearchBar
                placeHolder={TITLES.MODULE_NAME}
                addButtonDialog={handleMenuClick}
                onClearTextInput={handleClearSearch}
                addButtonMessage={TITLES.ADD_MODULE}
                onSearchTextBoxChange={debounceSearch}
                showAddButton={createApplicationPermission}
              />
            </Box>
          </Box>
          <Box component="div" className={classes.listItemPaper}>
            <Loader isCenter showLoader={showLoader} customClasses={classes.loader} />
            {moduleList.length ?
              <SearchList
                dense={false}
                minHeight={70}
                addDivider={true}
                data={moduleList}
                height={height - 330}
                disableGutters={false}
                key={TITLES.MODULE_LIST}
                selectedItem={selectedModule}
                onClick={handleListItemClick}
                isLoading={loaderComponent === TITLES.MODULES}
                totalCount={selectedModulePagination.totalCount}
                currentPage={selectedModulePagination.pageNumber}
                onPageChange={(pageNo: number) => {
                  console.log("Page No: ", pageNo);
                  // getAllModuleList(pageNo);
                }}
                listStyle={classes.searchListContainer}
                renderItem={(item) => (
                  <SearchListItem
                    key={item?.id}
                    showAvatarContent={true}
                    ListPrimaryContent={
                      <Box>
                        <Typography variant="body1">
                          <Box data-testid="name" component="span" fontWeight="fontWeightBold">
                            {item?.name}
                          </Box>
                        </Typography>
                      </Box>
                    }
                    ListSecondaryContent={
                      <Box>
                        <Typography variant="body1" color="textSecondary">
                          <Box
                            data-testid="application-description"
                            component="span"
                            fontWeight="fontWeightNormal"
                            className={classes.textEllipsisSecondary}
                          >
                            {item.serviceId}
                          </Box>
                        </Typography>
                      </Box>
                    }
                    ListSecondaryActionContent={
                      (deleteModulePermission || editModulePermission) &&
                      <LuiThreeDotMenu items={_.compact(items)} ariaLabel="Three Dot Menu" />
                    }
                  />
                )}
              /> : null
            }
            {searchString.length > 0 && moduleList.length === 0 && (
              <EmptyComponent disableCreateAction title={MESSAGES.NO_RESULTS_FOUND} />
            )}
          </Box>
        </Box>
        {selectedModule?.id &&
          <Box className={classes.rightContainer}>
            <ModuleDetails />
          </Box>
        }
        {(moduleList && moduleList.length === 0) && searchString === "" &&
          <Box className={classes.rightContainer}>
            <EmptyComponent
              subTitle=""
              disableCreateAction={false}
              buttonText={MESSAGES.MODULE_ADD_BUTTON}
              title={MESSAGES.MODULE_EMPTY_SCREEN_TITLE}
              handleOnCreateAction={() => handleMenuClick("Add")}
            />
          </Box>
        }
        <AddModuleDialog
          isEdit={action === "Edit"}
          moduleData={selectedModule}
          refreshOnSave={() => { }}
          openCloseModal={handleCloseDialog}
          openModal={action === "Edit" || action === "Add"}
        />
        {action === "Delete" && (
          <AlertDialog
            showDialogLoader={false}
            handleOnOk={deleteModule}
            secondButtonText={TITLES.CANCEL}
            primaryButtonText={TITLES.DELETE}
            handleOnCancel={() => setAction("")}
            recordName={selectedModule.name}
            dialogContentText={TITLES.DELETE}
            dialogTitle={MESSAGES.DELETE_MODULE_TITLE}
          />
        )}
      </CheckAuthzPermissions>
    </Box>
  );
};

export default ModuleList;
